import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback, useEffect, useState } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--statuspage/statuspage-edit-spotlight-placement';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { Text } from '@atlaskit/primitives/compiled';

import Image from '@atlaskit/image';

import { FormattedMessage } from 'react-intl';

import editDetailsSpotlightImage from '../../../assets/edit_details_spotlight.png';

import { messages } from '../../../components/messages';

import { type MessageTemplateContext } from '../../../index';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightMedia } from '@atlaskit/spotlight/media';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

type RenderProps = MessageTemplateContext & PlacementRenderProps;

/**
 * DOM selector for status-page to provide when integrating.
 * Targets the edit / setup area.
 */
export const STATUSPAGE_EDIT_SPOTLIGHT_SELECTOR = '#statuspage-edit-spotlight-target';

const preventAutoDismiss = () => {};

export const render: {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (input: PlacementRenderProps & Record<string, never>) => JSX.Element | null;
} = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: ({ onDismiss }: RenderProps) => {
		const { messageDismissed } = useMessageEvent();
		const { onAcknowledged, onClosed } = usePostOfficeUserInteractions();

		const [isVisible, setIsVisible] = useState<boolean>(false);

		useEffect(() => {
			if (typeof window === 'undefined') {
				return;
			}

			const checkForElement = () => {
				const element = globalThis.document?.querySelector(STATUSPAGE_EDIT_SPOTLIGHT_SELECTOR);
				if (element) {
					setIsVisible(true);
				}
			};

			// Check immediately
			checkForElement();

			// Also check periodically in case element isn't ready yet
			const intervalId = setInterval(checkForElement, 100);

			// Clean up after 5 seconds
			const timeoutId = setTimeout(() => {
				clearInterval(intervalId);
			}, 5000);

			return () => {
				clearInterval(intervalId);
				clearTimeout(timeoutId);
			};
		}, []);

		const renderInline = useMountToHtmlElement({
			selectors: STATUSPAGE_EDIT_SPOTLIGHT_SELECTOR,
			as: 'inline',
			position: 'inside',
		});

		const dismiss = useCallback(async () => {
			await messageDismissed();
			void onClosed({ interactionIdentifier: 'dismiss' });
			onDismiss?.();
		}, [messageDismissed, onClosed, onDismiss]);

		const handlePrimaryAction = useCallback(async () => {
			await messageDismissed();
			void onAcknowledged({
				interactionIdentifier: 'done',
				additionalMetadata: { actionTaken: true },
			});
			onDismiss?.();
		}, [messageDismissed, onAcknowledged, onDismiss]);

		if (!isVisible) {
			return null;
		}

		return (
			<div>
				{renderInline(
					<PopoverProvider>
						<PopoverTarget>
							<div />
						</PopoverTarget>
						<PopoverContent dismiss={preventAutoDismiss} placement="right-end" isVisible={true}>
							<SpotlightCard>
								<SpotlightHeader>
									<SpotlightHeadline>
										<FormattedMessage {...messages.heading} />
									</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl autoFocus={false} onClick={dismiss} />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightMedia>
									<Image
										src={editDetailsSpotlightImage}
										alt="Status page created - customize your status page"
									/>
								</SpotlightMedia>
								<SpotlightBody>
									<Text>
										<FormattedMessage {...messages.body} />
									</Text>
								</SpotlightBody>
								<SpotlightFooter>
									<SpotlightActions>
										<SpotlightPrimaryAction onClick={handlePrimaryAction}>
											<FormattedMessage {...messages.primaryAction} />
										</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						</PopoverContent>
					</PopoverProvider>,
				)}
			</div>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
